import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type Project = {
  id: string;
  index: string;
  title: string;
  role: string;
  stack: string;
  year: string;
  url: string;
  image: string;
};

// Each image URL is a fully hardcoded literal — no template strings, no vars.
const projects: Project[] = [
  {
    id: "aurora",
    index: "01",
    title: "AURORA",
    role: "AUTONOMOUS RESEARCH AGENT",
    stack: "TS · PYTHON · LLM · POSTGRES",
    year: "2025",
    url: "https://example.com/aurora",
    image:
      "https://readdy.ai/api/search-image?query=Minimal%20editorial%20black%20and%20white%20photograph%20of%20a%20dark%20control%20room%20with%20softly%20glowing%20monitors%20displaying%20abstract%20data%20visualizations%2C%20cinematic%20lighting%2C%20architectural%20geometry%2C%20high%20contrast%20monochrome%20with%20a%20single%20warm%20highlight%2C%20heavy%20film%20grain%2C%20negative%20space%20and%20clean%20composition%20in%20the%20style%20of%20a%20fashion%20magazine%20spread&width=1400&height=900&seq=work-aurora-01&orientation=landscape",
  },
  {
    id: "helix",
    index: "02",
    title: "HELIX",
    role: "MULTI-TENANT INFERENCE STACK",
    stack: "GO · CUDA · KUBERNETES",
    year: "2025",
    url: "https://example.com/helix",
    image:
      "https://readdy.ai/api/search-image?query=Architectural%20photograph%20of%20a%20server%20rack%20interior%20with%20precise%20geometric%20lines%20and%20diagonal%20cables%2C%20monochrome%20warm%20paper%20tones%20with%20subtle%20shadows%2C%20high%20detail%20industrial%20design%2C%20clean%20composition%20with%20intentional%20negative%20space%2C%20editorial%20photography%20style%2C%20matte%20finish%20and%20soft%20natural%20lighting%20from%20above&width=1400&height=900&seq=work-helix-02&orientation=landscape",
  },
  {
    id: "meridian",
    index: "03",
    title: "MERIDIAN",
    role: "EVAL / TELEMETRY PIPELINE",
    stack: "RUST · CLICKHOUSE · KAFKA",
    year: "2024",
    url: "https://example.com/meridian",
    image:
      "https://readdy.ai/api/search-image?query=Long%20exposure%20photograph%20of%20flowing%20light%20trails%20over%20a%20dark%20metallic%20surface%2C%20cinematic%20noir%20tones%2C%20precise%20architectural%20framing%2C%20high%20contrast%20black%20and%20white%20with%20subtle%20yellow%20green%20highlights%2C%20editorial%20magazine%20aesthetic%2C%20heavy%20grain%20texture%20and%20negative%20space%2C%20abstract%20industrial%20composition&width=1400&height=900&seq=work-meridian-03&orientation=landscape",
  },
  {
    id: "kepler",
    index: "04",
    title: "KEPLER",
    role: "REALTIME EDITOR / PAIR MODE",
    stack: "REACT · WEBSOCKET · CRDT",
    year: "2024",
    url: "https://example.com/kepler",
    image:
      "https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20minimal%20wooden%20desk%20with%20a%20single%20mechanical%20keyboard%20and%20warm%20paper%20notebook%2C%20soft%20natural%20light%20from%20the%20left%2C%20muted%20monochrome%20warm%20tones%2C%20high%20detail%20texture%2C%20architectural%20geometry%20and%20clean%20negative%20space%2C%20fashion%20magazine%20aesthetic%20with%20subtle%20film%20grain%20and%20quiet%20composition&width=1400&height=900&seq=work-kepler-04&orientation=landscape",
  },
  {
    id: "vector",
    index: "05",
    title: "VECTOR",
    role: "AGENT ORCHESTRATOR CLI",
    stack: "PYTHON · OPENAI · DOCKER",
    year: "2024",
    url: "https://example.com/vector",
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20photograph%20of%20intersecting%20geometric%20planes%20in%20matte%20black%20with%20one%20subtle%20yellow%20green%20accent%20line%2C%20architectural%20diagonal%20composition%2C%20heavy%20negative%20space%2C%20high%20contrast%20editorial%20photography%2C%20cinematic%20noir%20lighting%2C%20monochrome%20with%20single%20signal%20highlight%2C%20industrial%20magazine%20cover%20aesthetic&width=1400&height=900&seq=work-vector-05&orientation=landscape",
  },
  {
    id: "orbital",
    index: "06",
    title: "ORBITAL",
    role: "VECTOR MEMORY LAYER",
    stack: "TS · PGVECTOR · REDIS",
    year: "2023",
    url: "https://example.com/orbital",
    image:
      "https://readdy.ai/api/search-image?query=Editorial%20architectural%20photograph%20of%20concentric%20metallic%20rings%20in%20warm%20paper%20monochrome%20tones%2C%20soft%20directional%20lighting%2C%20precise%20geometric%20composition%20with%20clean%20negative%20space%2C%20subtle%20shadows%20and%20industrial%20matte%20finish%2C%20fashion%20magazine%20spread%20aesthetic%2C%20high%20detail%20texture%20and%20quiet%20cinematic%20framing&width=1400&height=900&seq=work-orbital-06&orientation=landscape",
  },
];

export default function SceneWork() {
  const root = useRef<HTMLDivElement>(null);
  const preview = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);
  const [hovered, setHovered] = useState(false);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      // heading + rows enter as scroll passes
      gsap.from(".work-heading", {
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          end: "top 20%",
          scrub: 0.8,
        },
        yPercent: 60,
        opacity: 0,
        letterSpacing: "0.1em",
      });

      gsap.from(".work-row", {
        scrollTrigger: {
          trigger: ".work-list",
          start: "top 80%",
          end: "bottom 40%",
          scrub: 0.8,
        },
        yPercent: 40,
        opacity: 0,
        stagger: 0.06,
      });

      // horizontal marker line stretches from left as you scroll
      gsap.from(".work-rule", {
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          end: "top 20%",
          scrub: 0.8,
        },
        scaleX: 0,
        transformOrigin: "left center",
      });
    }, root);

    return () => ctx.revert();
  }, []);

  // Track cursor to move the preview image
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };
    window.addEventListener("mousemove", onMove);

    let raf = 0;
    const loop = () => {
      const p = preview.current;
      if (p) {
        const currentX = parseFloat(p.dataset.x || "0");
        const currentY = parseFloat(p.dataset.y || "0");
        // ease toward cursor for smooth follow
        const nx = currentX + (mouse.current.x - currentX) * 0.12;
        const ny = currentY + (mouse.current.y - currentY) * 0.12;
        p.dataset.x = String(nx);
        p.dataset.y = String(ny);
        p.style.transform = `translate3d(${nx}px, ${ny}px, 0) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="scene-work"
      ref={root}
      data-theme="dark"
      className="relative w-full ink-bg grain overflow-hidden"
    >
      {/* Top meta */}
      <div className="relative z-10 px-6 md:px-10 pt-28 md:pt-32 flex items-start justify-between">
        <div className="type-label text-[color:var(--gray)]">
          <div>CHAPTER · 04</div>
          <div className="mt-1 text-[color:var(--paper)]">SELECTED WORK</div>
        </div>
        <div className="type-label text-[color:var(--gray)] text-right hidden md:block">
          <div>{String(projects.length).padStart(2, "0")} PROJECTS</div>
          <div className="mt-1 text-[color:var(--paper)]">HOVER · CLICK</div>
        </div>
      </div>

      {/* Section heading — huge editorial type */}
      <div className="relative z-10 px-6 md:px-10 mt-10 md:mt-14 grid grid-cols-12 items-end gap-6">
        <div className="col-span-12 md:col-span-8">
          <div
            className="work-heading type-display text-[color:var(--paper)]"
            style={{ fontSize: "clamp(64px, 14vw, 220px)", letterSpacing: "-0.05em" }}
          >
            WORK
            <span className="text-[color:var(--signal)]">.</span>
          </div>
        </div>
        <div className="col-span-12 md:col-span-4 pb-3">
          <div className="type-mono text-[11px] text-[color:var(--gray)] max-w-sm leading-relaxed">
            A running index of systems shipped as one person.
            Hover a title to preview the artefact —
            <span className="text-[color:var(--paper)]"> click to open the case.</span>
          </div>
        </div>
      </div>

      {/* horizontal rule */}
      <div className="work-rule relative z-10 mx-6 md:mx-10 mt-10 h-px bg-[color:var(--paper)]/25" />

      {/* Column headers */}
      <div className="relative z-10 mx-6 md:mx-10 mt-4 grid grid-cols-12 type-label text-[color:var(--gray)] pb-3 border-b border-[color:var(--paper)]/10">
        <div className="col-span-1">IDX</div>
        <div className="col-span-5 md:col-span-4">PROJECT</div>
        <div className="hidden md:block md:col-span-4">ROLE / STACK</div>
        <div className="col-span-4 md:col-span-2 text-right md:text-left">YEAR</div>
        <div className="col-span-2 md:col-span-1 text-right">GO</div>
      </div>

      {/* List */}
      <ul
        className="work-list relative z-10 mx-6 md:mx-10"
        onMouseLeave={() => {
          setActive(null);
          setHovered(false);
        }}
      >
        {projects.map((p, i) => (
          <li
            key={p.id}
            className="work-row group relative border-b border-[color:var(--paper)]/10"
            onMouseEnter={() => {
              setActive(i);
              setHovered(true);
            }}
          >
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${p.title} case study`}
              className="grid grid-cols-12 items-center py-6 md:py-8 cursor-pointer"
            >
              <div className="col-span-1 type-mono text-[11px] text-[color:var(--gray)] tabular">
                {p.index}
              </div>

              <div className="col-span-5 md:col-span-4 relative overflow-hidden">
                <div
                  className="type-display text-[color:var(--paper)] transition-transform duration-500 ease-out will-change-transform"
                  style={{
                    fontSize: "clamp(28px, 4.6vw, 84px)",
                    letterSpacing: "-0.04em",
                    lineHeight: 0.9,
                    transform:
                      active === i ? "translateX(12px)" : "translateX(0)",
                  }}
                >
                  {p.title}
                </div>
                {/* revealing underline */}
                <div
                  className="absolute left-0 bottom-2 h-px bg-[color:var(--signal)] origin-left transition-transform duration-500 ease-out"
                  style={{
                    width: "72%",
                    transform: active === i ? "scaleX(1)" : "scaleX(0)",
                  }}
                />
              </div>

              <div className="hidden md:block md:col-span-4 type-mono text-[12px] text-[color:var(--gray)] leading-snug">
                <div className="text-[color:var(--paper)]">{p.role}</div>
                <div className="mt-1 opacity-70">{p.stack}</div>
              </div>

              <div className="col-span-4 md:col-span-2 type-mono text-[12px] text-[color:var(--gray)] tabular text-right md:text-left">
                {p.year}
              </div>

              <div className="col-span-2 md:col-span-1 flex items-center justify-end type-mono text-[12px] text-[color:var(--paper)]">
                <span
                  className="inline-flex items-center gap-2 transition-transform duration-500 ease-out"
                  style={{
                    transform:
                      active === i ? "translateX(-6px)" : "translateX(0)",
                  }}
                >
                  <span className="hidden md:inline text-[color:var(--gray)]">OPEN</span>
                  <span
                    className="w-6 h-6 flex items-center justify-center border transition-colors duration-300"
                    style={{
                      borderColor:
                        active === i
                          ? "var(--signal)"
                          : "rgba(241,239,232,0.35)",
                      color:
                        active === i ? "var(--signal)" : "var(--paper)",
                    }}
                  >
                    <i className="ri-arrow-right-up-line text-[13px]" />
                  </span>
                </span>
              </div>
            </a>

            {/* subtle bg wash on hover */}
            <div
              className="absolute inset-0 pointer-events-none transition-opacity duration-500"
              style={{
                background:
                  "linear-gradient(90deg, rgba(255,90,31,0.06), transparent 40%)",
                opacity: active === i ? 1 : 0,
              }}
            />
          </li>
        ))}
      </ul>

      {/* Footer meta */}
      <div className="relative z-10 mx-6 md:mx-10 mt-12 md:mt-16 pb-24 md:pb-32 flex items-end justify-between">
        <div className="type-mono text-[11px] text-[color:var(--gray)] max-w-xs leading-relaxed">
          → MORE ARCHIVE ENTRIES AVAILABLE ON REQUEST.
          <br />
          <span className="text-[color:var(--paper)]">
            hello@remi.ai
          </span>
        </div>
        <a
          href="mailto:hello@remi.ai"
          className="type-mono text-[12px] text-[color:var(--paper)] inline-flex items-center gap-3 whitespace-nowrap group cursor-pointer"
        >
          <span className="opacity-60 group-hover:opacity-100 transition-opacity">
            REQUEST FULL INDEX
          </span>
          <span className="w-8 h-px bg-[color:var(--paper)] transition-all duration-300 group-hover:w-12 group-hover:bg-[color:var(--signal)]" />
        </a>
      </div>

      {/* Floating preview image (follows cursor) */}
      <div
        ref={preview}
        aria-hidden="true"
        className="fixed top-0 left-0 pointer-events-none z-40 transition-opacity duration-500"
        style={{
          width: "clamp(280px, 28vw, 460px)",
          height: "clamp(180px, 20vw, 300px)",
          opacity: hovered && active !== null ? 1 : 0,
        }}
      >
        <div className="relative w-full h-full overflow-hidden border border-[color:var(--paper)]/20">
          {projects.map((p, i) => (
            <img
              key={p.id}
              src={p.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500"
              style={{
                opacity: active === i ? 1 : 0,
              }}
              draggable={false}
            />
          ))}
          {/* film-grain overlay to blend into ink theme */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(rgba(9,10,10,0.35) 1px, transparent 1px)",
              backgroundSize: "3px 3px",
              mixBlendMode: "multiply",
            }}
          />
          {/* corner label */}
          <div className="absolute top-2 left-2 type-label text-[color:var(--paper)] bg-[color:var(--ink)]/70 px-1.5 py-0.5">
            {active !== null ? projects[active].index : ""} / PREVIEW
          </div>
        </div>
      </div>
    </section>
  );
}